import {
  WppActionButton,
  WppCard,
  WppCheckbox,
  WppDivider,
  WppIconAvailableCheckmark,
  WppIconMore,
  WppIconTrash,
  WppListItem,
  WppMenuContext,
  WppSkeleton,
} from '@platform-ui-kit/components-library-react'
import { Tenant } from '@wpp-open/core'
import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Avatar } from 'components/common/avatar/Avatar'
import { Flex } from 'components/common/flex/Flex'
import { showNotificationItemSideModal } from 'components/notificationItemSideModal/NotificationItemSideModal'
import styles from 'components/notificationsSideModal/notificationCard/NotificationCard.module.scss'
import {
  AppsNotificationCardContent,
  CustomNotificationCardContent,
  HierarchyAccessInfoCardContent,
  HierarchyRequestedCardContent,
  HubAccessGrantedCardContent,
  HubRequestedCardContent,
  HubRequestedCompiledCardContent,
  NewNotificationCardContent,
  ProjectInviteCardContent,
  TaskChanged,
  ProjectAssignmentCardContent,
  TenantRequestedCardContent,
  UpcomingEventCardContent,
  ProjectDeadlineCardContent,
  ProjectStatusChangedCardContent,
} from 'components/notificationsSideModal/notificationCard/NotificationCardContent'
import { NotificationHeader } from 'components/notificationsSideModal/notificationHeader/NotificationHeader'
import { useModifyFeed, useNotificationFeedsState } from 'components/notificationsSideModal/utils'
import { systemApps } from 'constants/apps'
import { showViewEventSideModal } from 'pages/home/systemWidgets/calendarWidget/viewEventSideModal/ViewEventSideModal'
import { useOsRoot } from 'providers/osRoot/OsRootContext'
import { SystemAppCode } from 'types/apps/microApps'
import { EventName, Feed } from 'types/feeds/feeds'
import { TenantShort } from 'types/tenants/tenant'

interface Props {
  notification: Feed
  tenant: TenantShort | Tenant
}

export const NotificationCard = ({ notification, tenant }: Props) => {
  const { t } = useTranslation()
  const { id, isRead, context, createdAt, eventName } = notification
  const { logoThumbnail, id: tenantId, name } = tenant
  const {
    hub_id,
    event_id,
    tenant_name,
    user_name,
    request_reason,
    reject_reason,
    body,
    title,
    hub_name,
    starts_at: startsAt,
    ends_at: endsAt,
  } = context

  const [isAvatarHover, setIsAvatarHover] = useState(false)
  const { selectedFeeds, handleFeedSelect } = useNotificationFeedsState()

  const { defaultTenant } = useOsRoot()

  const logoUrl = logoThumbnail?.url || defaultTenant.logoThumbnail?.url
  const isCheckBoxVisible = isAvatarHover || selectedFeeds.some(({ id: _id }) => _id === id)

  const notificationTitle = eventName === EventName.CALENDAR_UPCOMING_EVENT ? t('os.notification.calendar') : title

  const { readFeed, deleteFeed } = useModifyFeed()
  const navigate = useNavigate()

  const handleCardClick = async () => {
    if (notification.eventName === EventName.NEWS_CREATED_NOTIFICATION) {
      navigate(`${systemApps[SystemAppCode.News].osRoute}/${context.news_item_id}`)
    } else if (eventName === EventName.CALENDAR_UPCOMING_EVENT) {
      showViewEventSideModal({ eventId: event_id, tenantId, readOnly: true, hub_id: hub_id! })
    } else {
      showNotificationItemSideModal({ notification, logoUrl })
    }
    if (!isRead) {
      await readFeed(id, tenantId, true)
    }
  }

  const handleReadStatus = async () => {
    await readFeed(id, tenantId, !isRead)
  }

  const handleFeedDelete = async () => {
    await deleteFeed(id, tenantId)
  }

  return (
    <div className={styles.cardWrapper} data-testid="notification-card">
      <Flex
        className={styles.avatarWrapper}
        onMouseOver={() => setIsAvatarHover(true)}
        onMouseOut={() => setIsAvatarHover(false)}
      >
        {isCheckBoxVisible ? (
          <WppCheckbox
            name="isSelected"
            className={styles.checkBox}
            onWppChange={() => handleFeedSelect({ id, isRead })}
            data-testid="notification-select-checkbox"
          />
        ) : (
          <Avatar
            size="m"
            name={name}
            src={logoUrl || undefined}
            className={styles.avatar}
            data-testid="notification-card-logo"
          />
        )}
      </Flex>

      <WppCard size="s" onClick={handleCardClick} className={styles.card} data-testid="notification-card-content">
        <Flex direction="column" gap={4} className={styles.content}>
          {eventName === EventName.CALENDAR_UPCOMING_EVENT && (
            <>
              <NotificationHeader createdAt={createdAt} title={notificationTitle} />
              <UpcomingEventCardContent title={title} startsAt={startsAt} endsAt={endsAt} />
            </>
          )}

          {eventName === EventName.HIERARCHY_ACCESS_REQUESTED_ADMIN && (
            <>
              <NotificationHeader createdAt={createdAt} />
              <HierarchyRequestedCardContent userName={user_name} />
            </>
          )}

          {(eventName === EventName.HIERARCHY_ACCESS_REJECTED || eventName === EventName.HIERARCHY_ACCESS_APPROVED) && (
            <>
              <NotificationHeader createdAt={createdAt} />
              <HierarchyAccessInfoCardContent
                eventName={eventName}
                requestReason={request_reason}
                rejectReason={reject_reason}
              />
            </>
          )}

          {eventName === EventName.TENANT_ACCESS_REQUESTED_ADMIN && (
            <>
              <NotificationHeader createdAt={createdAt} />
              <TenantRequestedCardContent userName={user_name} tenantName={tenant_name} />
            </>
          )}

          {eventName === EventName.ADMIN_NOTIFICATION_REQUEST && (
            <>
              <NotificationHeader createdAt={createdAt} />
              <CustomNotificationCardContent text={body} />
            </>
          )}
          {eventName === EventName.APPS_NOTIFICATION && (
            <>
              <NotificationHeader createdAt={createdAt} title={title} />
              <AppsNotificationCardContent body={body} />
            </>
          )}
          {eventName === EventName.NEWS_CREATED_NOTIFICATION && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.news')} />
              <NewNotificationCardContent text={body} />
            </>
          )}
          {eventName === EventName.HUB_ACCESS_GRANTED && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.hub', { hubName: hub_name })} />
              <HubAccessGrantedCardContent hubName={context?.hub_name} />
            </>
          )}
          {eventName === EventName.HUB_ACCESS_REQUESTED_ADMIN && (
            <>
              <NotificationHeader
                title={t('os.notification.access.hub.admin_title', { hubName: context?.hub_name })}
                createdAt={createdAt}
              />
              <HubRequestedCardContent userName={user_name} hubName={context?.hub_name} />
            </>
          )}
          {(eventName === EventName.HUB_ACCESS_REJECTED || eventName === EventName.HUB_ACCESS_APPROVED) && (
            <>
              <NotificationHeader
                title={t('os.notification.access.hub.admin_title', { hubName: context?.hub_name })}
                createdAt={createdAt}
              />
              <HubRequestedCompiledCardContent
                hubName={context?.hub_name}
                eventName={eventName}
                requestReason={request_reason}
                rejectReason={reject_reason}
              />
            </>
          )}
          {eventName === EventName.PROJECT_MEMBER_INVITED && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.projects')} />
              <ProjectInviteCardContent
                projectName={context?.project_name}
                userName={context?.project_owner_fullname}
              />
            </>
          )}
          {(eventName === EventName.PROJECT_TASK_STATUS_CHANGED || eventName === EventName.PROJECT_TASK_UPDATED) && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.projects')} />
              <TaskChanged
                isStatusChanged={eventName === EventName.PROJECT_TASK_STATUS_CHANGED}
                userName={context?.author_name}
                projectName={context?.project_name}
                taskName={context?.task_name}
                statusName={context?.task_status}
              />
            </>
          )}
          {(eventName === EventName.PROJECT_MEMBER_ASSIGNED || eventName === EventName.PROJECT_MEMBER_UNASSIGNED) && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.projects')} />
              <ProjectAssignmentCardContent
                isUnassignment={eventName === EventName.PROJECT_MEMBER_UNASSIGNED}
                context={context}
              />
            </>
          )}
          {(eventName === EventName.PROJECT_TASK_UPCOMING_DEADLINE ||
            eventName === EventName.PROJECT_TASK_OVERDUE_DEADLINE) && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.projects')} />
              <ProjectDeadlineCardContent
                isOverdue={eventName === EventName.PROJECT_TASK_OVERDUE_DEADLINE}
                context={context}
              />
            </>
          )}
          {eventName === EventName.PROJECT_STATUS_CHANGED && (
            <>
              <NotificationHeader createdAt={createdAt} title={t('os.notification.projects')} />
              <ProjectStatusChangedCardContent context={context} />
            </>
          )}
        </Flex>
      </WppCard>

      <Flex className={styles.actionsWrapper} align="center" gap={8}>
        <WppMenuContext
          dropdownConfig={{
            appendTo: () => document.body,
            placement: 'bottom-end',
          }}
        >
          <WppActionButton
            slot="trigger-element"
            variant="secondary"
            className={styles.actionButton}
            data-testid="notification-card-actions"
          >
            <WppIconMore direction="horizontal" className={styles.moreIcon} />
          </WppActionButton>
          <Flex direction="column" gap={8}>
            <WppListItem onWppChangeListItem={() => handleReadStatus()}>
              <WppIconAvailableCheckmark slot="left" />
              <span slot="label">
                {t(isRead ? 'os.notification.actions.mark_as_unread' : 'os.notification.actions.mark_as_read')}
              </span>
            </WppListItem>
            <WppDivider />
            <WppListItem onWppChangeListItem={() => handleFeedDelete()}>
              <WppIconTrash slot="left" />
              <span slot="label">{t('os.notification.actions.delete')}</span>
            </WppListItem>
          </Flex>
        </WppMenuContext>
        <div className={clsx(styles.unread, !isRead && styles.visibile)} data-testid="notification-unread-indicator" />
      </Flex>
    </div>
  )
}

export const NotificationCardSkeleton = () => {
  return (
    <WppCard>
      <Flex gap={20}>
        <WppSkeleton width={40} height={40} />
        <Flex direction="column" gap={5}>
          <WppSkeleton width={200} height={15} />
          <WppSkeleton width={400} height={20} />
          <WppSkeleton width={300} height={20} />
        </Flex>
      </Flex>
    </WppCard>
  )
}
